/**
 * Where the teaser sits, and the geometry of its sticky-bar anchor.
 *
 * Pure functions, so the host (`iframe-host.ts`) only measures and applies.
 *
 * - `bubble`: beside the round bubble in the corner (bubble mode, and the
 *   mobile shell in sticky-bar mode, where the app renders the bubble too).
 * - `bar`: directly above the sticky bar, left-aligned with it.
 * - `collapsed`: the sticky bar collapsed into its circle on scroll; beside
 *   that circle, which sits inside the strip's horizontal padding.
 *
 * The bar is rendered inside the iframe (`Launcher.tsx`), or before the
 * handoff by the host placeholder, which mirrors it. Both are a full-width
 * strip fixed to the bottom: 8px top padding (`pt-2`), 16px side padding
 * (`px-4`), and a centred surface `min(100% - 32px, clamp(42rem, 40vw, 54rem))`
 * wide (`--dhsv-sticky-surface-width` in `iframe-content.scss`, at the
 * iframe's 16px rem, where `vw` is the strip width).
 */

export type TeaserAnchor = 'bubble' | 'bar' | 'collapsed';

export const STICKY_STRIP_PADDING_X_PX = 16;
export const STICKY_STRIP_PADDING_TOP_PX = 8;
export const STICKY_SURFACE_MIN_PX = 672;
export const STICKY_SURFACE_MAX_PX = 864;
export const STICKY_SURFACE_VW = 0.4;
/** The teaser above the bar is never wider than this (22rem). */
export const BAR_TEASER_MAX_WIDTH_PX = 352;

export type Rect = { left: number; top: number; width: number };

export type BarTeaserGeometry = {
	/** Left edge of the teaser, in viewport pixels. */
	left: number;
	/** Distance from the bottom of the viewport to the top of the bar. */
	bottom: number;
	/** The teaser's maximum width: the bar's width, capped. */
	maxWidth: number;
};

/**
 * Which anchor the teaser uses.
 *
 * @param {Object}                input
 * @param {'bubble'|'sticky_bar'} input.launcherMode    The configured launcher mode.
 * @param {boolean}               input.stickyBarActive The sticky bar is in play (not the mobile shell).
 * @param {'bar'|'bubble'}        input.launcherState   The sticky launcher's visual state.
 * @return {TeaserAnchor} The anchor.
 */
export function resolveTeaserAnchor( input: {
	launcherMode: 'bubble' | 'sticky_bar';
	stickyBarActive: boolean;
	launcherState: 'bar' | 'bubble';
} ): TeaserAnchor {
	if ( input.launcherMode !== 'sticky_bar' || ! input.stickyBarActive ) {
		return 'bubble';
	}
	return input.launcherState === 'bar' ? 'bar' : 'collapsed';
}

/**
 * The bar's own rectangle inside the bottom strip.
 *
 * @param {Rect} strip The strip (iframe or placeholder) in viewport pixels.
 * @return {Rect} The bar surface in viewport pixels.
 */
export function stickyBarRect( strip: Rect ): Rect {
	const inner = Math.max( 0, strip.width - 2 * STICKY_STRIP_PADDING_X_PX );
	const preferred = Math.min(
		STICKY_SURFACE_MAX_PX,
		Math.max( STICKY_SURFACE_MIN_PX, strip.width * STICKY_SURFACE_VW )
	);
	const width = Math.min( inner, preferred );
	return {
		left: strip.left + ( strip.width - width ) / 2,
		top: strip.top + STICKY_STRIP_PADDING_TOP_PX,
		width,
	};
}

/**
 * Geometry of the teaser above the sticky bar, or null when there is nothing
 * to anchor to (a strip with no width, or a bar below the viewport).
 *
 * @param {Object} input
 * @param {Rect}   input.strip          The strip in viewport pixels.
 * @param {number} input.viewportHeight The layout viewport height.
 * @return {BarTeaserGeometry|null} Where to put the teaser.
 */
export function barTeaserGeometry( input: {
	strip: Rect;
	viewportHeight: number;
} ): BarTeaserGeometry | null {
	const { strip, viewportHeight } = input;
	if (
		! Number.isFinite( strip.width ) ||
		! Number.isFinite( strip.top ) ||
		strip.width <= 0 ||
		viewportHeight <= 0
	) {
		return null;
	}
	const bar = stickyBarRect( strip );
	if ( bar.width <= 0 || bar.top >= viewportHeight ) {
		return null;
	}
	return {
		left: Math.round( bar.left ),
		bottom: Math.max( 0, Math.round( viewportHeight - bar.top ) ),
		maxWidth: Math.floor( Math.min( bar.width, BAR_TEASER_MAX_WIDTH_PX ) ),
	};
}
